<template>
  <div class="w-full h-full space-y-4">
    <Card>
      <CardHeader>
        <CardTitle>Themes</CardTitle>
        <CardDescription>
          Reusable colour sets. Open a theme to edit it against a live preview; link it to any
          number of <router-link to="/whitelabel/sites" class="underline">sites</router-link> - edits
          apply to every site on it.
        </CardDescription>
        <CardAction>
          <Button size="sm" @click="openCreate">
            <Plus class="w-4 h-4" /> New theme
          </Button>
        </CardAction>
      </CardHeader>
    </Card>

    <Card>
      <CardContent class="pt-6">
        <DataTable
          :headers="headers"
          :items="themes"
          :loading="loading"
          searchable
          search-placeholder="Search themes…"
          @row-click="openTheme"
        >
          <template #item-name="theme">
            <span class="font-medium">{{ theme.name || 'Untitled theme' }}</span>
          </template>
          <template #item-colours="theme">
            <span class="inline-flex items-center gap-1">
              <span
                v-for="key in swatchKeys"
                :key="key"
                class="size-4 rounded-full border border-border"
                :style="{ background: theme[key] || 'transparent' }"
                :title="theme[key]"
              />
            </span>
          </template>
          <template #item-updatedOn="{ updatedOn }">
            <span class="text-muted-foreground">{{ formatDate(updatedOn) }}</span>
          </template>
          <template #item-action="theme">
            <Button variant="ghost" size="sm" @click.stop="openTheme(theme)">
              Edit <ChevronRight class="w-4 h-4" />
            </Button>
          </template>
        </DataTable>
      </CardContent>
    </Card>

    <!-- Name-first create -->
    <AlertDialog :open="showCreate" @update:open="(v) => { showCreate = v }">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Name your theme</AlertDialogTitle>
          <AlertDialogDescription>Give the theme a name - you can change it later.</AlertDialogDescription>
        </AlertDialogHeader>
        <div class="grid gap-1.5 py-1">
          <Label for="new-theme-name">Theme name</Label>
          <Input id="new-theme-name" v-model="newName" placeholder="e.g. Boogi Blue" @keyup.enter="confirmCreate" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel :disabled="creating" @click="showCreate = false">Cancel</AlertDialogCancel>
          <AlertDialogAction :disabled="!newName.trim() || creating" @click="confirmCreate">
            {{ creating ? 'Creating…' : 'Create' }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<script>
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
  Button, DataTable, Input, Label,
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
  toast,
} from '@boogi/ui'
import { Plus, ChevronRight } from 'lucide-vue-next'

export default {
  name: 'WhitelabelThemes',
  components: {
    Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
    Button, DataTable, Input, Label,
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
    Plus, ChevronRight,
  },
  data() {
    return {
      loading: true,
      creating: false,
      showCreate: false,
      newName: '',
      themes: [],
      headers: [
        { text: 'Theme', value: 'name', sortable: true },
        { text: 'Colours', value: 'colours' },
        { text: 'Updated', value: 'updatedOn', sortable: true },
        { text: '', value: 'action', width: 90 },
      ],
      swatchKeys: ['primaryColour', 'secondaryColour', 'backgroundColour', 'buttonColour'],
    }
  },
  created() {
    this.load()
  },
  mounted() {
    document.title = 'Dashboard | Themes'
  },
  methods: {
    base() {
      return localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
    },
    authHeaders(json = true) {
      const h = { Authorization: `${localStorage.getItem('token')}` }
      if (json) h['Content-Type'] = 'application/json'
      return h
    },
    api(path = '') {
      return `${this.base()}/private/api/client/whitelabel/themes${path}`
    },
    formatDate(v) {
      if (!v) return '-'
      try {
        return new Date(v).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      } catch {
        return '-'
      }
    },
    openTheme(theme) {
      if (theme && theme.id) this.$router.push(`/whitelabel/themes/${theme.id}`)
    },
    async load() {
      this.loading = true
      try {
        const res = await this.$apiFetch(this.api(), { method: 'GET', headers: this.authHeaders() })
        if (res.ok) this.themes = await res.json()
        else toast.error('Could not load themes.')
      } catch (e) {
        toast.error('Could not load themes.')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.newName = ''
      this.showCreate = true
    },
    async confirmCreate() {
      const name = this.newName.trim()
      if (!name || this.creating) return
      this.creating = true
      try {
        const body = {
          name,
          primaryColour: '#1a66ff', secondaryColour: '#0b3fb0', backgroundColour: '#ffffff',
          textColour: '#0d162b', buttonColour: '#111827', buttonTextColour: '#ffffff',
          borderRadius: '14px',
        }
        const res = await this.$apiFetch(this.api(), { method: 'POST', headers: this.authHeaders(), body: JSON.stringify(body) })
        if (res.ok) {
          const t = await res.json()
          this.showCreate = false
          this.$router.push(`/whitelabel/themes/${t.id}`)
        } else toast.error('Could not create theme.')
      } catch (e) {
        toast.error('Could not create theme.')
      } finally {
        this.creating = false
      }
    },
  },
}
</script>
